import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { DocumentRecord } from '../../models/Document'
import { User } from '../../models/User'

import { RootState } from '../store'
import { DocumentType } from '../../models/DocumentType'
import { FileInfo } from '../media/file'

export interface DocumentFileInfo {
  fileId: string
  fileName: string
  fileSize: number
  contentType: string
}

interface DocumentState {
  isLoading: boolean
  pollingEnabled: boolean
  document: DocumentRecord | null
  creator: User | null
  creatorIsLoading: boolean
  contact: User | null
  contactIsLoading: boolean
  selectedDocumentType: DocumentType | null
  fileInfo: FileInfo | DocumentFileInfo | null
  fileInfoIsLoading: boolean
}

const initialState: DocumentState = {
  isLoading: true,
  pollingEnabled: false,
  document: null,
  creator: null,
  creatorIsLoading: true,
  contact: null,
  contactIsLoading: true,
  selectedDocumentType: null,
  fileInfo: null,
  fileInfoIsLoading: false,
}

export const documentSlice = createSlice({
  name: 'document',
  initialState,
  reducers: {
    setDocumentLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setDocument: (state, action: PayloadAction<DocumentRecord>) => {
      state.document = action.payload
    },
    startDocumentPolling: (state) => {
      state.pollingEnabled = true
    },
    stopDocumentPolling: (state) => {
      state.pollingEnabled = false
    },
    setDocumentCreator: (state, action: PayloadAction<User>) => {
      state.creator = action.payload
    },
    setDocumentCreatorIsLoading: (state, action: PayloadAction<boolean>) => {
      state.creatorIsLoading = action.payload
    },
    setDocumentContact: (state, action: PayloadAction<User>) => {
      state.contact = action.payload
    },
    setDocumentContactIsLoading: (state, action: PayloadAction<boolean>) => {
      state.contactIsLoading = action.payload
    },
    setSelectedDocumentType: (state, action: PayloadAction<DocumentType>) => {
      state.selectedDocumentType = action.payload
    },
    setDocumentFileInfo: (state, action: PayloadAction<FileInfo | DocumentFileInfo>) => {
      state.fileInfo = action.payload
    },
    setDocumentFileInfoIsLoading: (state, action: PayloadAction<boolean>) => {
      state.fileInfoIsLoading = action.payload
    },
    resetDocument: () => initialState,
  },
})

export const {
  setDocumentLoading,
  setDocument,
  startDocumentPolling,
  stopDocumentPolling,
  setDocumentCreator,
  setDocumentCreatorIsLoading,
  setDocumentContact,
  setDocumentContactIsLoading,
  setSelectedDocumentType,
  setDocumentFileInfo,
  setDocumentFileInfoIsLoading,
  resetDocument,
} = documentSlice.actions

export const selectDocument = (state: RootState) => state.processDocument.document.document

export const selectDocumentLoading = (state: RootState) => state.processDocument.document.isLoading

export const selectDocumentPollingEnabled = (state: RootState) => state.processDocument.document.pollingEnabled

export const selectDocumentCreator = (state: RootState) => state.processDocument.document.creator

export const selectDocumentCreatorIsLoading = (state: RootState) => state.processDocument.document.creatorIsLoading

export const selectDocumentContact = (state: RootState) => state.processDocument.document.contact

export const selectDocumentContactIsLoading = (state: RootState) => state.processDocument.document.contactIsLoading

export const selectSelectedDocumentType = (state: RootState) => state.processDocument.document.selectedDocumentType

export const selectDocumentFileInfo = (state: RootState) => state.processDocument.document.fileInfo

export const selectDocumentFileInfoIsLoading = (state: RootState) => state.processDocument.document.fileInfoIsLoading
